import { CheckCheck, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { AiActionItem } from "@/components/cockpit/AiActionItem";
import { errText } from "@/components/pipeline/utils";

const SORTS = [["priority", "Priorité"], ["owner", "Commercial"], ["date", "Plus récentes"]];

const Stats = ({ stats }) => {
  if (!stats?.by_type?.length) return null;
  const sum = (k) => stats.by_type.reduce((n, t) => n + (t[k] || 0), 0);
  return <p className="ck-muted" data-testid="ai-stats">Historique : {sum("validated")} validées (dont {sum("edited")} modifiées) · {sum("ignored")} ignorées · {sum("pending")} en attente</p>;
};

// Onglet « À valider » du Cockpit : propositions de l'IA, jamais exécutées sans validation
export const AiActionsPanel = ({ onCount }) => {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("priority");
  const [owner, setOwner] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const load = useCallback(() => {
    api.get("/ai-actions", { params: { sort, ...(owner ? { owner } : {}) } }).then(({ data: d }) => { setData(d); onCount?.(d.pending); }).catch((e) => toast.error(errText(e)));
    api.get("/ai-actions/stats").then(({ data: d }) => setStats(d)).catch(() => {});
  }, [sort, owner, onCount]);
  useEffect(() => { load(); }, [load]);
  const remove = (ids) => {
    setData((d) => { const n = { ...d, items: d.items.filter((p) => !ids.includes(p.id)), pending: Math.max(d.pending - ids.length, 0) }; onCount?.(n.pending); return n; });
    setSel((s) => s.filter((x) => !ids.includes(x)));
  };
  const run = async (fn, ids, ok) => {
    setBusy(true);
    try { const res = await fn(); toast.success(ok(res)); remove(ids); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const validate = (p, content) => run(() => api.post(`/ai-actions/${p.id}/validate`, { content }).then((r) => r.data), [p.id], (r) => `${data.labels[p.type]} : ${r.result}`);
  const ignore = (p, reason) => run(() => api.post(`/ai-actions/${p.id}/ignore`, { reason }), [p.id], () => "Proposition ignorée.");
  const bulk = async (action) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/ai-actions/bulk", { ids: sel, action });
      toast[r.errors.length ? "warning" : "success"](`${r.done} proposition(s) ${action === "validate" ? "validée(s)" : "ignorée(s)"}${r.errors.length ? `, ${r.errors.length} en erreur : ${r.errors[0].error}` : ""}.`);
      setSel([]); load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (!data) return <p className="ck-muted">Chargement…</p>;
  const all = data.items.length > 0 && sel.length === data.items.length;
  return (
    <div className="ai-panel" data-testid="ai-actions-panel">
      <div className="ai-toolbar">
        <label className="ai-all"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : data.items.map((p) => p.id))} data-testid="ai-select-all" /> Tout sélectionner</label>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="ai-sort">{SORTS.map(([k, l]) => <option key={k} value={k}>Tri : {l}</option>)}</select>
        {data.owners.length > 1 && <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="ai-owner"><option value="">Commercial : tous</option>{data.owners.map((o) => <option key={o} value={o}>{o}</option>)}</select>}
        {sel.length > 0 && <span className="ai-bulk" data-testid="ai-bulk-bar"><b>{sel.length} sélectionnée(s)</b><button type="button" className="primary-button" disabled={busy} onClick={() => bulk("validate")} data-testid="ai-bulk-validate"><CheckCheck size={14} /> Valider la sélection</button><button type="button" className="secondary-button" disabled={busy} onClick={() => bulk("ignore")} data-testid="ai-bulk-ignore"><X size={14} /> Ignorer</button></span>}
      </div>
      <Stats stats={stats} />
      {!data.items.length ? <p className="ck-empty" data-testid="ai-empty"><Sparkles size={16} /> Aucune action à valider : l'IA en proposera après les prochaines activités.</p> : (
        <div className="ck-list">
          {data.items.map((p) => <AiActionItem key={p.id} p={p} label={data.labels[p.type]} busy={busy} checked={sel.includes(p.id)} onCheck={() => setSel((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]))} onValidate={(c) => validate(p, c)} onIgnore={(r) => ignore(p, r)} />)}
        </div>
      )}
    </div>
  );
};
